import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, Validators } from '@angular/forms';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { PostcodeService } from '@src/app/core/services/postcode.service';
import { Address } from '@src/app/models/address';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormComponent } from '../form/form/form.component';
import { add } from 'lodash';

@Component({
  selector: 'app-address-lookup',
  templateUrl: './address-lookup.component.html',
  styleUrls: ['./address-lookup.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AddressLookupComponent,
      multi: true
    }
  ]
})

export class AddressLookupComponent extends FormComponent implements OnInit, ControlValueAccessor, OnChanges {
  @Input() public address: Address = {} as Address;
  @Input() public manualEntry = false;
  @Output() public addressChanged = new EventEmitter<Address>();
  
  public form: FormGroup = new FormGroup({});
  public formInputPostcode: FormGroup = new FormGroup({});
  public showSearchMessage = false;
  public editMode = true;
  public addressId = 0;
  public postcodeInput = '';
  public addresses: Address[] = [];

  override validationMessages = {
    street: {
      required: 'Street is required'
    },
    city: {
      required: 'Town is required'
    },
    postcode: {
      required: 'Postcode is required'
    }
  };

  override onSubmit(): void {
    // Not used in this component
  }

  constructor(
    private postcodeService: PostcodeService,
    private loadingSpinnerService: LoadingSpinnerService,
    private fb: FormBuilder
  ) {
    super();
  }

  override ngOnInit(): void {

     this.generateForm();

    this.form.valueChanges.subscribe(value => {
      if(this.address === this.form.value)
        return;
      this.address =  { ...this.address, ...this.form.value };
      this.address.id = 0;  // reset the id to 0 to ensure a new address is created
      this.addressChanged.emit(this.address);
    });
     // The onAddressIdChange method will be called within generateForm to ensure proper setup
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['address']) {
      var newAddress = changes['address'].currentValue;
      let previousAddress = changes['address'].previousValue;
      let isFirstChange = changes['address'].isFirstChange
      if(newAddress && !this.areAddressesEqual(newAddress, previousAddress))
        this.patchAddress(newAddress);
    }
  }

  public generateForm(): void {
    this.formInputPostcode = this.fb.group({
      postcodeInput: [this.postcodeInput]
    });

    // Create form without validators initially
    this.form = this.fb.group({
      addressId: [this.address?.id || null],
      flatNo: [this.address?.flatNo],
      buildingName: [this.address?.buildingName],
      number: [this.address?.number],
      street: [this.address?.street],
      city: [this.address?.city],
      county: [this.address?.county],
      postCode: [this.address?.postCode?.toUpperCase()],
      country: [this.address?.country]
    });

    // Subscribe to form value changes to dynamically add/remove validators
    this.form.valueChanges.subscribe(() => {
      const shouldValidate = this.hasAddressValues();
      
      // Update validators based on condition
      const streetControl = this.form.get('street');
      const cityControl = this.form.get('city');
      const postCodeControl = this.form.get('postCode');

      if (shouldValidate) {
        streetControl?.setValidators(Validators.required);
        cityControl?.setValidators(Validators.required);
        postCodeControl?.setValidators(Validators.required);
      } else {
        streetControl?.clearValidators();
        cityControl?.clearValidators();
        postCodeControl?.clearValidators();
      }

      // Update validation status
      streetControl?.updateValueAndValidity({ emitEvent: false });
      cityControl?.updateValueAndValidity({ emitEvent: false });
      postCodeControl?.updateValueAndValidity({ emitEvent: false });
    });

    this.onAddressIdChange(); // Ensure this is called after the form is created
  }

  public hasAddressValues(): boolean {
    return this.form.controls['flatNo'].value ||
           this.form.controls['buildingName'].value ||
           this.form.controls['number'].value ||
           this.form.controls['street'].value ||
           this.form.controls['city'].value ||
           this.form.controls['county'].value ||
           this.form.controls['postCode'].value ||
           this.form.controls['country'].value;
  }

  public toggleEditMode(): void {
    this.editMode = !this.editMode;
  }

  public onAddressIdChange(): void {
    this.form.get('addressId')?.valueChanges.subscribe((selectedId: string) => {
      const selectedAddress = this.addresses.find(address => address.fullAddress === selectedId);
      if (selectedAddress) {
        this.address = selectedAddress;
        console.log('address:' + JSON.stringify(this.address));
        this.patchAddress(this.address);
      }
    });
    this.addressChanged.emit(this.address);
  }

  private patchAddress(address: Address): void {
    if (address) {
      this.form.patchValue({
        flatNo: address.flatNo,
        buildingName: address.buildingName,
        number: address.number,
        street: address.street,
        city: address.city,
        county: address.county,
        postCode: address.postCode,
        country: address.country
      });
    }
  }

  private areAddressesEqual(address1: Address, address2: Address): boolean {
    return address1?.flatNo === address2?.flatNo &&
           address1?.buildingName === address2?.buildingName &&
           address1?.number === address2?.number &&
           address1?.street === address2?.street &&
           address1?.city === address2?.city &&
           address1?.county === address2?.county &&
           address1?.postCode === address2?.postCode &&
           address1?.country === address2?.country;
  }

  public onClick(): void {
    this.postcodeInput = this.formInputPostcode.controls['postcodeInput'].value;
    if (this.postcodeInput.length > 0) {
      this.showSearchMessage = false;
      this.loadingSpinnerService.show();

      this.address = {} as Address;

      this.postcodeService
        .Lookup(this.postcodeInput)
        .subscribe((addressLookup: any) => {
          this.showSearchMessage = true;
          addressLookup.addresses.forEach((apiAddress: any) => {
            apiAddress.latitude = addressLookup.latitude;
            apiAddress.longitude = addressLookup.longitude;
          });

          this.addresses = addressLookup.addresses;
          this.loadingSpinnerService.hide();
        });
    }
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this.addressId = value;
      this.form.get('addressId')?.setValue(this.addressId, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.form.get('addressId')?.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    // Implementation for handling touched state
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  public showAddressFields(): boolean {
    return this.hasAddressValues() || this.manualEntry;
  }

  public toggleManualEntry(): void {
    this.manualEntry = !this.manualEntry;
  }
}
